import React, { useState, useEffect, useCallback } from 'react';
import Card from '../../components/common/Card/Card';
import Table from '../../components/common/Table/Table';
import StatusBadge from '../../components/common/StatusBadge/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { getAllProjects } from '../../services/projectService';
import { getAssignmentsByProject } from '../../services/assignmentService';
import { getAllMentors } from '../../services/mentorService';
import { getAllStudents } from '../../services/studentService';
import { formatDate } from '../../utils/helpers';
import styles from './Assignments.module.css';
import { sortProjectsByName } from '../../utils/sortProjects';

const Assignments = () => {
  const [allProjects, setAllProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedMentor, setSelectedMentor] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  const filterProjects = useCallback(() => {
    let filtered = [...allProjects];

    if (selectedMentor) {
      filtered = filtered.filter(p => p.mentor_id === selectedMentor);
    }

    if (selectedStudent) {
      filtered = filtered.filter(p => p.student_id === selectedStudent);
    }

    setFilteredProjects(filtered);
    
    if (selectedProject && !filtered.find(p => p.id === selectedProject)) {
      setSelectedProject('');
    }
  }, [allProjects, selectedMentor, selectedStudent, selectedProject]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterProjects();
  }, [filterProjects]);

  useEffect(() => {
    if (selectedProject) {
      fetchAssignments(selectedProject);
    } else {
      setAssignments([]);
    }
  }, [selectedProject]);

  const fetchData = async () => {
    try {
      const [projectsData, mentorsData, studentsData] = await Promise.all([
        getAllProjects(),
        getAllMentors(),
        getAllStudents()
      ]);
      setAllProjects(projectsData);
      setFilteredProjects(projectsData);
      setMentors(mentorsData);
      setStudents(studentsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async (projectId) => {
    try {
      const data = await getAssignmentsByProject(projectId);
      console.log('Fetched assignments with submission data:', data);
      setAssignments(data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const columns = [
    { header: 'Title', accessor: 'title' },
    { header: 'Assigned Date', accessor: 'assigned_date', render: (row) => formatDate(row.assigned_date) },
    { header: 'Deadline', accessor: 'deadline', render: (row) => formatDate(row.deadline) },
    { 
      header: 'Max Score', 
      accessor: 'max_score', 
      render: (row) => (
        <span style={{ fontWeight: 600, color: row.max_score > 0 ? '#4CAF50' : '#999' }}>
          {row.max_score > 0 ? row.max_score : 'N/A'}
        </span>
      )
    },
    { 
      header: 'Score',
      accessor: 'submission_score',
      sortable: false,
      render: (row) => {
        const score = row.submission_score;
        const maxScore = row.max_score || 0;
        
        if (score !== null && score !== undefined && maxScore > 0) {
          const isPartial = row.submission_status === 'returned';
          return (
            <span style={{ 
              fontWeight: 700, 
              color: isPartial ? '#9C27B0' : '#388E3C',
              fontSize: '15px'
            }}>
              {score}/{maxScore}
              {isPartial && <span style={{ fontSize: '11px', marginLeft: '4px' }}>(Partial)</span>}
            </span>
          );
        }
        
        if (maxScore === 0) {
          return <span style={{ color: '#999', fontStyle: 'italic' }}>N/A</span>;
        }
        
        return <span style={{ color: '#999' }}>-</span>;
      }
    },
    { 
      header: 'Status', 
      render: (row) => <StatusBadge status={row.submission_status || 'assigned'} /> 
    },
    { 
      header: 'Feedback',
      accessor: 'submission_feedback',
      sortable: false,
      render: (row) => {
        if (row.submission_feedback && row.submission_feedback.trim()) {
          return (
            <span style={{ 
              color: '#4CAF50', 
              fontSize: '18px',
              cursor: 'pointer'
            }} title={row.submission_feedback}>
              Yes
            </span>
          );
        }
        return <span style={{ color: '#ccc', fontSize: '16px' }}>—</span>;
      }
    }
  ];

  if (loading) {
    return <div className={styles.loading}><LoadingSpinner /></div>;
  }

  return (
    <div className={styles.assignmentsPage}>
      <h1>Assignments</h1>

      <div className={styles.filterSection}>
        <div className={styles.formGroup}>
          <label>Select Mentor</label>
          <select value={selectedMentor} onChange={(e) => setSelectedMentor(e.target.value)} className={styles.select}>
            <option value="">-- Select --</option>
            {mentors.map(mentor => (
              <option key={mentor.id} value={mentor.id}>
                {mentor.first_name} {mentor.last_name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label>Select Student</label>
          <select value={selectedStudent} onChange={(e) => setSelectedStudent(e.target.value)} className={styles.select}>
            <option value="">-- Select --</option>
            {students.map(student => (
              <option key={student.id} value={student.id}>
                {student.first_name} {student.last_name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label>Select Project</label>
          <select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} className={styles.select}>
            <option value="">-- Select --</option>
            {sortProjectsByName(filteredProjects).map(project => (
              <option key={project.id} value={project.id}>
                {project.project_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedProject ? (
        <Card>
          <Table columns={columns} data={assignments} />
        </Card>
      ) : (
        <Card>
          <p className={styles.empty}>Please select a project to view assignments</p>
        </Card>
      )}
    </div>
  );
};

export default Assignments;